/**
 * R3AdminClient — every fetch that hits an `/api/admin/…` endpoint.
 *
 * Split out from `R3Client` so the two surfaces don't share a single 2 k-line
 * file. Both classes import their HTTP helpers from `./r3-client-base` so
 * anything cross-cutting (error handling, retries, auth headers) stays in one
 * place. Types stay declared in `./r3-client` and are re-imported here — the
 * one-way flow is `r3-client-base ← r3-client ← r3-admin-client`, no cycles.
 */

import type { ReviewReviewersConfig } from '@shared/review-reviewers-config'
import type { ReviewSessionStatus } from '@shared/review-status-config'
import type { ReviewBenchRun } from '@shared/reviewbench'
import type { AdminReplicationRunData, ScreeningResultData } from '@shared/types/review'

import type { AdminApiBilling, AdminBillingSettings, ApiKeySummary } from '@server/services/api-billing'

import type { AdminNotificationOverview } from './r3-client'
import type {
  AdminActivityDashboardStats,
  AdminStageCostAudience,
  AdminStageCostStats,
  AdminErrorFrequencyRow,
  AdminErrorPatternRow,
  AdminObservingDashboard,
  AdminObservingScheduleDetail,
  AdminOrganization,
  AdminOrganizationDetail,
  AdminOrganizationFilter,
  AdminOrgMember,
  AdminOrgSessionRow,
  AdminServiceAccount,
  AdminServiceAccountFilter,
  AdminSessionDetail,
  AdminSessionIncidentRow,
  AdminSessionRow,
  AdminSessionTimeline,
  AdminSlackChannel,
  AdminStageErrorEntry,
  AdminUnmatchedErrorSample,
  AdminUnmatchedUserSample,
  AdminUserDeletionPreview,
  AdminUserDeletionResult,
  AdminUserDetail,
  AdminUserFilter,
  AdminUserGroupMatch,
  AdminUserGroupRow,
  AdminUserHealthReport,
  AdminUserMembership,
  AdminUserPagedRow,
  AdminUserPlan,
  AdminWatchlistItem,
  BiorxivOverview,
  Evaluation,
  EvaluationHumanComment,
  EvaluationHumanCommentWithReviewer,
  EvaluationHumanReview,
  EvaluationSummary,
  Reviewer3CommentForEvaluation,
  BulkAction,
} from './r3-client'
import { json, request, requestVoid } from './r3-client-base'

export class R3AdminClient {
  static runEvaluationReviewBench(evaluationId: string) {
    return request<{ started: boolean }>(
      `/api/admin/evaluations/${encodeURIComponent(evaluationId)}/reviewbench`,
      json({})
    )
  }

  static getEvaluationReviewBenchStatus(evaluationId: string) {
    return request<{ running: boolean; run: ReviewBenchRun | null; executionStatus: string }>(
      `/api/admin/evaluations/${encodeURIComponent(evaluationId)}/reviewbench`
    )
  }
  // ── Admin (superadmin only) ────────────────────────────────────────────────

  static listAdminOrganizations(
    since: string | null = null,
    opts: {
      offset?: number
      limit?: number
      q?: string
      filter?: AdminOrganizationFilter
      sortBy?: 'name' | 'code' | 'type' | 'createdAt'
      sortDir?: 'asc' | 'desc'
    } = {}
  ) {
    const qs = new URLSearchParams()
    if (since) qs.set('since', since)
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset))
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit))
    if (opts.q) qs.set('q', opts.q)
    if (opts.filter && opts.filter !== 'all') qs.set('filter', opts.filter)
    if (opts.sortBy) qs.set('sortBy', opts.sortBy)
    if (opts.sortDir) qs.set('sortDir', opts.sortDir)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<{
      organizations: AdminOrganization[]
      total: number
      offset: number
      limit: number
      filter: AdminOrganizationFilter
    }>(`/api/admin/organizations${suffix}`)
  }

  static createAdminOrganization(input: {
    name: string
    code: string
    type: 'university' | 'publisher' | 'journal'
    key?: string
    parentCode?: string
  }) {
    return request<{ organization: { id: string; code: string } }>('/api/admin/organizations', json(input))
  }

  /** Back-compat search for the UserPicker — wraps `listAdminUsers({ q })`. */
  static async searchAdminUsers(query: string): Promise<{ users: AdminUserPagedRow[] }> {
    const { users } = await R3AdminClient.listAdminUsers({ q: query, limit: 25 })
    return { users }
  }

  static listAdminUsers(
    opts: {
      q?: string
      filter?: AdminUserFilter
      offset?: number
      limit?: number
      sortBy?: 'email' | 'name' | 'createdAt'
      sortDir?: 'asc' | 'desc'
    } = {}
  ) {
    const qs = new URLSearchParams()
    if (opts.q) qs.set('q', opts.q)
    if (opts.filter && opts.filter !== 'all') qs.set('filter', opts.filter)
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset))
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit))
    if (opts.sortBy) qs.set('sortBy', opts.sortBy)
    if (opts.sortDir) qs.set('sortDir', opts.sortDir)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<{
      users: AdminUserPagedRow[]
      total: number
      offset: number
      limit: number
      filter: AdminUserFilter
    }>(`/api/admin/users${suffix}`)
  }

  static updateAdminUserMeta(userId: string, meta: unknown) {
    return request<{ id: string; meta: unknown }>(
      `/api/admin/users/${encodeURIComponent(userId)}/meta`,
      json({ meta }, 'PATCH')
    )
  }

  static setAdminUserMaxConcurrent(userId: string, value: number | null) {
    return request<{ id: string; maxConcurrentReviews: number | null }>(
      `/api/admin/users/${encodeURIComponent(userId)}/max-concurrent`,
      json({ value }, 'PATCH')
    )
  }

  /** What a delete would remove, plus the external refs needing manual cleanup. Read-only. */
  static getAdminUserDeletionPreview(userId: string) {
    return request<AdminUserDeletionPreview>(`/api/admin/users/${encodeURIComponent(userId)}/deletion-preview`)
  }

  /**
   * Irreversibly delete a user. `confirmEmail` must match the target's email —
   * the server rejects the request otherwise.
   */
  static deleteAdminUser(userId: string, confirmEmail: string) {
    return request<AdminUserDeletionResult>(
      `/api/admin/users/${encodeURIComponent(userId)}?confirmEmail=${encodeURIComponent(confirmEmail)}`,
      { method: 'DELETE' }
    )
  }

  static grantAdminUserPremium(userId: string) {
    return request<{ id: string; hasActiveSubscription: boolean }>(
      `/api/admin/users/${encodeURIComponent(userId)}/premium`,
      { method: 'POST' }
    )
  }

  static revokeAdminUserPremium(userId: string) {
    return request<{ id: string; hasActiveSubscription: boolean }>(
      `/api/admin/users/${encodeURIComponent(userId)}/premium`,
      { method: 'DELETE' }
    )
  }

  static updateAdminOrganizationMeta(code: string, meta: unknown) {
    return request<{ id: string; meta: unknown }>(
      `/api/admin/organizations/${encodeURIComponent(code)}/meta`,
      json({ meta }, 'PATCH')
    )
  }

  static setAdminOrganizationNudgeEmailsDisabled(code: string, disabled: boolean) {
    return request<{ disabled: boolean }>(
      `/api/admin/organizations/${encodeURIComponent(code)}/nudge-emails`,
      json({ disabled }, 'PATCH')
    )
  }

  static updateAdminServiceAccountMeta(id: string, meta: unknown) {
    return request<{ id: string; meta: unknown }>(
      `/api/admin/service-accounts/${encodeURIComponent(id)}/meta`,
      json({ meta }, 'PATCH')
    )
  }

  // ── Entity search (used by AddSubscriberDialog) ─────────────────────────

  static searchAdminWatchlist(query: string) {
    return request<{ results: AdminWatchlistItem[] }>(`/api/admin/watchlist?q=${encodeURIComponent(query)}`)
  }

  static getAdminUserDetail(userId: string, since: string | null = null) {
    const suffix = since ? `?since=${encodeURIComponent(since)}` : ''
    return request<{
      user: AdminUserDetail
      memberships: AdminUserMembership[]
      plan: AdminUserPlan
    }>(`/api/admin/users/${encodeURIComponent(userId)}${suffix}`)
  }

  static getAdminActivity(since: string | null) {
    const suffix = `?since=${encodeURIComponent(since ?? 'all')}`
    return request<{
      since: string | undefined
      stats: AdminActivityDashboardStats
    }>(`/api/admin/activity${suffix}`)
  }

  static getAdminStageCosts(since: string | null, audience: AdminStageCostAudience) {
    const query = `?since=${encodeURIComponent(since ?? 'all')}&audience=${audience}`
    return request<{
      since: string | undefined
      audience: AdminStageCostAudience
      stats: AdminStageCostStats
    }>(`/api/admin/stage-costs${query}`)
  }

  static getAdminUserHealth(months = 12) {
    return request<AdminUserHealthReport>(`/api/admin/user-health?months=${months}`)
  }

  static listAdminSessions(filter?: {
    userId?: string
    organizationId?: string
    sessionId?: string
    status?: ReviewSessionStatus
    q?: string
    /** ISO timestamp or null for "all time". */
    since?: string | null
    offset?: number
    limit?: number
    sortBy?: 'id' | 'title' | 'status' | 'author' | 'org' | 'mode' | 'revisions' | 'createdAt'
    sortDir?: 'asc' | 'desc'
  }) {
    const qs = new URLSearchParams()
    if (filter?.userId) qs.set('userId', filter.userId)
    if (filter?.organizationId) qs.set('organizationId', filter.organizationId)
    if (filter?.sessionId) qs.set('sessionId', filter.sessionId)
    if (filter?.status) qs.set('status', filter.status)
    if (filter?.q) qs.set('q', filter.q)
    qs.set('since', filter?.since ?? 'all')
    if (filter?.offset !== undefined) qs.set('offset', String(filter.offset))
    if (filter?.limit !== undefined) qs.set('limit', String(filter.limit))
    if (filter?.sortBy) qs.set('sortBy', filter.sortBy)
    if (filter?.sortDir) qs.set('sortDir', filter.sortDir)
    return request<{
      since: string | undefined
      sessions: AdminSessionRow[]
      total: number
      offset: number
      limit: number
    }>(`/api/admin/sessions?${qs.toString()}`)
  }

  static getAdminSessionDetail(sessionId: string) {
    return request<{ session: AdminSessionDetail }>(`/api/admin/sessions/${encodeURIComponent(sessionId)}`)
  }

  static getAdminSessionReplication(sessionId: string) {
    return request<{ runs: AdminReplicationRunData[] }>(
      `/api/admin/sessions/${encodeURIComponent(sessionId)}/replication`
    )
  }

  static getAdminSessionTimeline(sessionId: string, workflowId?: string) {
    const suffix = workflowId ? `?workflowId=${encodeURIComponent(workflowId)}` : ''
    return request<{
      timeline: AdminSessionTimeline
      stageErrorHistory: AdminStageErrorEntry[]
    }>(`/api/admin/sessions/${encodeURIComponent(sessionId)}/timeline${suffix}`)
  }

  static listAdminErrorPatterns() {
    return request<{ patterns: AdminErrorPatternRow[] }>('/api/admin/error-patterns')
  }

  static createAdminErrorPattern(input: { pattern: string; label: string }) {
    return request<{ pattern: AdminErrorPatternRow }>('/api/admin/error-patterns', json(input))
  }

  static updateAdminErrorPattern(id: string, input: { pattern?: string; label?: string }) {
    return request<{ pattern: AdminErrorPatternRow }>(
      `/api/admin/error-patterns/${encodeURIComponent(id)}`,
      json(input, 'PATCH')
    )
  }

  static deleteAdminErrorPattern(id: string) {
    return request<{ ok: true }>(`/api/admin/error-patterns/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  }

  static getAdminErrorFrequency(since: string | null) {
    const suffix = `?since=${encodeURIComponent(since ?? 'all')}`
    return request<{
      since: string | undefined
      frequencies: AdminErrorFrequencyRow[]
      unmatchedSamples: AdminUnmatchedErrorSample[]
    }>(`/api/admin/error-frequency${suffix}`)
  }

  static getAdminSessionIncidentTimeline(sessionId: string) {
    return request<{ sessionId: string; rows: AdminSessionIncidentRow[] }>(
      `/api/admin/session-incidents/${encodeURIComponent(sessionId)}/timeline`
    )
  }

  static listAdminUserGroups() {
    return request<{ groups: AdminUserGroupRow[] }>('/api/admin/user-groups')
  }

  static createAdminUserGroup(input: { pattern: string; label: string }) {
    return request<{ group: AdminUserGroupRow }>('/api/admin/user-groups', json(input))
  }

  static updateAdminUserGroup(id: string, input: { pattern?: string; label?: string }) {
    return request<{ group: AdminUserGroupRow }>(
      `/api/admin/user-groups/${encodeURIComponent(id)}`,
      json(input, 'PATCH')
    )
  }

  static deleteAdminUserGroup(id: string) {
    return request<{ ok: true }>(`/api/admin/user-groups/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  }

  static getAdminUserGroupMatches() {
    return request<{
      matches: AdminUserGroupMatch[]
      unmatchedSamples: AdminUnmatchedUserSample[]
    }>('/api/admin/user-groups/matches')
  }

  static getAdminObservingDashboard() {
    return request<AdminObservingDashboard>('/api/admin/observability')
  }

  static getAdminObservingRunsPage(input: { cursor?: string; pageSize?: number; sortDir?: 'asc' | 'desc' }) {
    const params = new URLSearchParams()
    if (input.cursor) params.set('cursor', input.cursor)
    if (input.pageSize) params.set('pageSize', String(input.pageSize))
    if (input.sortDir) params.set('sortDir', input.sortDir)
    const qs = params.toString()
    return request<{
      runs: AdminObservingDashboard['recentRuns']
      nextCursor: string | null
      total: number
    }>(`/api/admin/observability/runs${qs ? `?${qs}` : ''}`)
  }

  static getAdminObservingSchedule() {
    return request<{ schedule: AdminObservingScheduleDetail }>('/api/admin/observability/schedule')
  }

  static putAdminObservingSchedule(input: { intervalAmount: number; intervalUnit: 'minutes' | 'hours' | 'days' }) {
    return request<{ scheduleId: string; action: 'created' | 'updated' }>(
      '/api/admin/observability/schedule',
      json(input, 'PUT')
    )
  }

  static deleteAdminObservingSchedule() {
    return request<{ deleted: boolean }>('/api/admin/observability/schedule', {
      method: 'DELETE',
    })
  }

  static getAdminObservabilityGlobalConfig() {
    return request<{ config: { displayTimezone: string; baseAlertMinutes: number } }>(
      '/api/admin/observability/global-config'
    )
  }

  static putAdminObservabilityGlobalConfig(input: { displayTimezone?: string; baseAlertMinutes?: number }) {
    return request<{ config: { displayTimezone: string; baseAlertMinutes: number } }>(
      '/api/admin/observability/global-config',
      json(input, 'PUT')
    )
  }

  static launchAdminObservingRun(
    input: ({ taskIds: string[] } | { targets: Array<{ taskId: string; entityIds?: string[] }> }) & {
      simulatedTimeIso?: string
    }
  ) {
    return request<{ workflowId: string; runId: string }>('/api/admin/observability/run', json(input))
  }

  static clearAdminObservingAlertLog(input: { type?: string; all?: boolean }) {
    const params = new URLSearchParams()
    if (input.all) params.set('all', '1')
    else if (input.type) params.set('type', input.type)
    return request<{ deleted: number }>(`/api/admin/observability/alert-log?${params.toString()}`, {
      method: 'DELETE',
    })
  }

  // ── Observe task schedule overrides ─────────────────────────────────────

  static upsertObserveTaskSchedule(input: {
    taskId: string
    intervalAmount: number
    intervalUnit: 'minutes' | 'hours' | 'days' | 'weeks'
    enabled: boolean
  }) {
    return request<{ schedule: unknown }>('/api/admin/observe-tasks', json(input, 'PATCH'))
  }

  static resetObserveTaskSchedule(taskId: string) {
    return request<{ ok: boolean }>(`/api/admin/observe-tasks?taskId=${encodeURIComponent(taskId)}`, {
      method: 'DELETE',
    })
  }

  // ── Slack channel inventory ──────────────────────────────────────────────

  static listAdminSlackChannels(q?: string) {
    const suffix = q ? `?q=${encodeURIComponent(q)}` : ''
    return request<{ channels: AdminSlackChannel[] }>(`/api/admin/slack-channels${suffix}`)
  }

  static createAdminSlackChannel(input: { slackId: string; name: string }) {
    return request<{ channel: AdminSlackChannel }>('/api/admin/slack-channels', json(input))
  }

  static updateAdminSlackChannel(id: string, input: Partial<{ name: string; slackId: string }>) {
    return request<{ channel: AdminSlackChannel }>(
      `/api/admin/slack-channels/${encodeURIComponent(id)}`,
      json(input, 'PATCH')
    )
  }

  static deleteAdminSlackChannel(id: string) {
    return request<{ ok: true }>(`/api/admin/slack-channels/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  }

  static getSupabaseTableIds() {
    return request<{ tables: Record<string, number> }>('/api/admin/supabase-tables')
  }

  static addAdminOrganizationMember(
    code: string,
    input: { email: string; role?: 'admin' | 'member'; sendInvite?: boolean }
  ) {
    return request<{ created: boolean; emailSent: boolean | null }>(
      `/api/admin/organizations/${encodeURIComponent(code)}/members`,
      json(input)
    )
  }

  static updateAdminOrgMemberRole(code: string, userId: string, role: 'admin' | 'member') {
    return request<{ member: { userId: string; role: string } }>(
      `/api/admin/organizations/${encodeURIComponent(code)}/members/${encodeURIComponent(userId)}`,
      json({ role }, 'PATCH')
    )
  }

  static removeAdminOrgMember(code: string, userId: string) {
    return requestVoid(`/api/admin/organizations/${encodeURIComponent(code)}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    })
  }

  static getAdminOrganizationDetail(code: string, since: string | null = null) {
    const suffix = since ? `?since=${encodeURIComponent(since)}` : ''
    return request<{
      organization: AdminOrganizationDetail
      members: AdminOrgMember[]
      children: AdminOrganization[]
      viewAs: { email: string; role: 'admin' | 'member' } | null
    }>(`/api/admin/organizations/${encodeURIComponent(code)}${suffix}`)
  }

  /** Every member of the org with session counts — the CSV export's source.
   *  The detail payload already carries this list; this is for re-fetching it
   *  uncapped at download time. */
  static listAdminOrganizationMembers(code: string, since: string | null = null) {
    const suffix = since ? `?since=${encodeURIComponent(since)}` : ''
    return request<{ members: AdminOrgMember[]; total: number }>(
      `/api/admin/organizations/${encodeURIComponent(code)}/members${suffix}`
    )
  }

  static listAdminOrganizationSessions(code: string, opts: { offset?: number; limit?: number } = {}) {
    const qs = new URLSearchParams()
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset))
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<{
      sessions: AdminOrgSessionRow[]
      total: number
      offset: number
      limit: number
    }>(`/api/admin/organizations/${encodeURIComponent(code)}/sessions${suffix}`)
  }

  // ── Admin: Service accounts ─────────────────────────────────────────────

  static listAdminServiceAccounts(
    since: string | null = null,
    opts: {
      offset?: number
      limit?: number
      q?: string
      filter?: AdminServiceAccountFilter
      sortBy?: 'name' | 'lastUsedAt' | 'createdAt'
      sortDir?: 'asc' | 'desc'
    } = {}
  ) {
    const qs = new URLSearchParams()
    if (since) qs.set('since', since)
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset))
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit))
    if (opts.q) qs.set('q', opts.q)
    if (opts.filter && opts.filter !== 'all') qs.set('filter', opts.filter)
    if (opts.sortBy) qs.set('sortBy', opts.sortBy)
    if (opts.sortDir) qs.set('sortDir', opts.sortDir)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<{
      accounts: AdminServiceAccount[]
      total: number
      offset: number
      limit: number
      filter: AdminServiceAccountFilter
    }>(`/api/admin/service-accounts${suffix}`)
  }

  static getAdminServiceAccount(id: string, since: string | null = null) {
    const suffix = since ? `?since=${encodeURIComponent(since)}` : ''
    return request<{ account: AdminServiceAccount; sessions: AdminSessionRow[]; keys: ApiKeySummary[] }>(
      `/api/admin/service-accounts/${encodeURIComponent(id)}${suffix}`
    )
  }

  static createAdminServiceAccount(input: {
    name: string
    description?: string | null
    permissions: string[]
    userId?: string | null
    actsForAnyUser?: boolean
  }) {
    return request<{ account: AdminServiceAccount; apiKey: string }>('/api/admin/service-accounts', json(input))
  }

  static updateAdminServiceAccount(
    id: string,
    input: {
      name?: string
      description?: string | null
      permissions?: string[]
      isActive?: boolean
      userId?: string | null
      actsForAnyUser?: boolean
      reviewersConfig?: ReviewReviewersConfig | null
    }
  ) {
    return request<{ account: AdminServiceAccount }>(
      `/api/admin/service-accounts/${encodeURIComponent(id)}`,
      json(input, 'PATCH')
    )
  }

  static deleteAdminServiceAccount(id: string) {
    return requestVoid(`/api/admin/service-accounts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  }

  static createAdminServiceAccountKey(id: string, label: string | null) {
    return request<{ apiKey: string; key: ApiKeySummary }>(
      `/api/admin/service-accounts/${encodeURIComponent(id)}/keys`,
      json({ label })
    )
  }

  static rotateAdminServiceAccountKey(id: string, keyId: string) {
    return request<{ apiKey: string; key: ApiKeySummary }>(
      `/api/admin/service-accounts/${encodeURIComponent(id)}/keys/${encodeURIComponent(keyId)}/rotate`,
      { method: 'POST' }
    )
  }

  static revokeAdminServiceAccountKey(id: string, keyId: string) {
    return requestVoid(`/api/admin/service-accounts/${encodeURIComponent(id)}/keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    })
  }

  static getAdminServiceAccountBilling(id: string) {
    return request<AdminApiBilling>(`/api/admin/service-accounts/${encodeURIComponent(id)}/billing`)
  }

  static updateAdminServiceAccountBilling(id: string, settings: AdminBillingSettings) {
    return requestVoid(`/api/admin/service-accounts/${encodeURIComponent(id)}/billing`, json(settings, 'PATCH'))
  }

  static addAdminServiceAccountLedgerEntry(
    id: string,
    entry: { type: 'grant' | 'adjustment'; amountCents: number; note: string }
  ) {
    return requestVoid(`/api/admin/service-accounts/${encodeURIComponent(id)}/billing/ledger`, json(entry))
  }

  // ── Admin: bioRxiv integration ──────────────────────────────────────────

  static getAdminBiorxivOverview() {
    return request<BiorxivOverview>('/api/admin/biorxiv')
  }

  static relaunchAdminBiorxivGroup(stem: string) {
    return request<{ moved: string[]; deleted: string[] }>('/api/admin/biorxiv/relaunch', json({ stem }))
  }

  static launchNewAdminBiorxiv(input: { stem: string; triggerXml: string }) {
    return request<{ uploaded: string[] }>('/api/admin/biorxiv/launch-new', json(input))
  }

  // ── Admin: Evaluations ──────────────────────────────────────────────────

  static listAdminEvaluations(orgCode: string) {
    return request<{ evaluations: EvaluationSummary[] }>(
      `/api/admin/organizations/${encodeURIComponent(orgCode)}/evaluations`
    )
  }

  static createAdminEvaluation(
    orgCode: string,
    input: { pdf: File; title?: string; reviewIntent?: string; screeningDecision?: 'approved' | 'rejected' | 'unknown' }
  ) {
    const form = new FormData()
    form.set('pdf', input.pdf)
    if (input.title) form.set('title', input.title)
    if (input.reviewIntent) form.set('reviewIntent', input.reviewIntent)
    if (input.screeningDecision) form.set('screeningDecision', input.screeningDecision)
    return request<{ evaluation: Evaluation }>(`/api/admin/organizations/${encodeURIComponent(orgCode)}/evaluations`, {
      method: 'POST',
      body: form,
    })
  }

  static bulkAdminEvaluations(orgCode: string, input: { evaluationIds: string[]; action: BulkAction }) {
    return request<{ affected: number }>(
      `/api/admin/organizations/${encodeURIComponent(orgCode)}/evaluations/bulk`,
      json(input)
    )
  }

  static getAdminEvaluation(evalId: string) {
    return request<{
      reviewIntent: string | null
      screeningResult: ScreeningResultData | null
      evaluation: Evaluation
      humanReviews: EvaluationHumanReview[]
      humanComments: EvaluationHumanCommentWithReviewer[]
      reviewer3Comments: Reviewer3CommentForEvaluation[]
      reviewer3SessionStatus: {
        reviewStatus: string | null
        derivedStatus: 'running' | 'completed' | 'error' | 'locked'
        hasTokens: boolean
      }
    }>(`/api/admin/evaluations/${encodeURIComponent(evalId)}`)
  }

  static updateAdminEvaluation(
    evalId: string,
    input: {
      title?: string
      sortOrder?: number
      manuscriptId?: string | null
      doi?: string | null
      submissionReceivedAt?: string | null
      revisedAt?: string | null
      acceptedAt?: string | null
      paperPublishedAt?: string | null
      roundsTotal?: number | null
      published?: boolean
    }
  ) {
    return request<{ evaluation: Evaluation }>(
      `/api/admin/evaluations/${encodeURIComponent(evalId)}`,
      json(input, 'PATCH')
    )
  }

  static deleteAdminEvaluation(evalId: string) {
    return requestVoid(`/api/admin/evaluations/${encodeURIComponent(evalId)}`, {
      method: 'DELETE',
    })
  }

  // ── Admin: Human reviewers & comments ───────────────────────────────────

  static addAdminEvaluationHumanReview(evalId: string, input: { reviewerName?: string | null } = {}) {
    return request<{ humanReview: EvaluationHumanReview }>(
      `/api/admin/evaluations/${encodeURIComponent(evalId)}/human-reviews`,
      json(input)
    )
  }

  static updateAdminHumanReview(reviewId: string, input: { reviewerName?: string | null; reviewerIndex?: number }) {
    return request<{ humanReview: EvaluationHumanReview }>(
      `/api/admin/human-reviews/${encodeURIComponent(reviewId)}`,
      json(input, 'PATCH')
    )
  }

  static deleteAdminHumanReview(reviewId: string) {
    return requestVoid(`/api/admin/human-reviews/${encodeURIComponent(reviewId)}`, {
      method: 'DELETE',
    })
  }

  static addAdminHumanComment(
    reviewId: string,
    input: {
      content: string
      title?: string | null
      citedText?: string | null
      authorResponse?: string | null
      severity?: number | null
      rank?: number | null
    }
  ) {
    return request<{ comment: EvaluationHumanComment }>(
      `/api/admin/human-reviews/${encodeURIComponent(reviewId)}/comments`,
      json(input)
    )
  }

  static updateAdminHumanComment(
    commentId: string,
    input: {
      content?: string
      title?: string | null
      citedText?: string | null
      authorResponse?: string | null
      severity?: number | null
      rank?: number | null
    }
  ) {
    return request<{ comment: EvaluationHumanComment }>(
      `/api/admin/human-comments/${encodeURIComponent(commentId)}`,
      json(input, 'PATCH')
    )
  }

  static deleteAdminHumanComment(commentId: string) {
    return requestVoid(`/api/admin/human-comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
    })
  }

  // ── Alert Rules (rule-based routing) ────────────────────────────────────

  static listAdminAlertRules() {
    return request<{ groups: Record<string, { default: AdminAlertRule | null; scoped: AdminAlertRule[] }> }>(
      '/api/admin/alert-rules'
    )
  }

  static createAdminAlertRuleDefault(input: { alertType: string; patch: AdminAlertRulePatch }) {
    return request<{ rule: AdminAlertRule }>('/api/admin/alert-rules', json({ ruleKind: 'default', ...input }))
  }

  static createAdminAlertRuleScoped(input: {
    alertType: string
    patch: AdminAlertRulePatch & {
      scope: Array<{ entityKind: AdminAlertRuleEntityKind; entityId: string }>
      slackChannelIds: string[]
    }
  }) {
    return request<{ rule: AdminAlertRule }>('/api/admin/alert-rules', json({ ruleKind: 'scoped', ...input }))
  }

  static updateAdminAlertRule(id: string, patch: AdminAlertRulePatch) {
    return request<{ rule: AdminAlertRule }>('/api/admin/alert-rules', json({ id, patch }, 'PATCH'))
  }

  static deleteAdminAlertRule(id: string) {
    return request<{ ok: boolean }>(`/api/admin/alert-rules?id=${encodeURIComponent(id)}`, { method: 'DELETE' })
  }

  // ── Notifications overview (rule-backed) ────────────────────────────────

  static getAdminNotifications(since?: string | null) {
    const qs = since ? `?since=${encodeURIComponent(since)}` : ''
    return request<AdminNotificationOverview>(`/api/admin/notifications${qs}`)
  }

  static sendNotificationTest(input: {
    alertType: string
    entity?: { kind: AdminAlertRuleEntityKind; id: string }
    broadcast?: boolean
    cadenceMinutes?: number
    mode?: 'real' | 'fake'
  }) {
    return request<{
      results: Array<{ target: string; delivered: boolean; reason: string }>
    }>('/api/admin/notifications/test', json(input))
  }

  // ── Meta groups ─────────────────────────────────────────────────────────

  static listMetaGroups() {
    return request<AdminMetaGroup[]>('/api/admin/meta-groups')
  }

  static getMetaGroup(id: string) {
    return request<AdminMetaGroup & { members: AdminMetaGroupMember[] }>(`/api/admin/meta-groups/${id}`)
  }

  static createMetaGroup(input: { name: string; meta?: Record<string, unknown> }) {
    return request<AdminMetaGroup>('/api/admin/meta-groups', json(input))
  }

  static updateMetaGroup(id: string, updates: { name?: string; meta?: Record<string, unknown> }) {
    return request<AdminMetaGroup>(`/api/admin/meta-groups/${id}`, json(updates, 'PATCH'))
  }

  static deleteMetaGroup(id: string) {
    return requestVoid(`/api/admin/meta-groups/${id}`, { method: 'DELETE' })
  }

  static addMetaGroupMember(id: string, member: { memberType: AdminMetaGroupMemberType; memberId: string }) {
    return request<{ success: boolean }>(`/api/admin/meta-groups/${id}/members`, json(member))
  }

  static removeMetaGroupMember(id: string, member: { memberType: AdminMetaGroupMemberType; memberId: string }) {
    return requestVoid(`/api/admin/meta-groups/${id}/members`, json(member, 'DELETE'))
  }

  static searchMetaGroupTargets(q: string) {
    return request<{ results: AdminMetaTarget[] }>(`/api/admin/meta-groups/search-targets?q=${encodeURIComponent(q)}`)
  }
}

/** Row returned by the meta-group target search — same shape as watchlist. */
export interface AdminMetaTarget {
  type: AdminMetaGroupMemberType
  id: string
  label: string
  sublabel: string | null
}

export type AdminMetaGroupMemberType = 'user' | 'org' | 'meta_group' | 'user_group' | 'service_account'

export interface AdminMetaGroup {
  id: string
  name: string
  meta: Record<string, unknown>
  createdAt: string
}

export interface AdminMetaGroupMember {
  groupId: string
  memberType: AdminMetaGroupMemberType
  memberId: string
  createdAt: string
}

export type AdminAlertRuleEntityKind =
  | 'user'
  | 'organization'
  | 'serviceAccount'
  | 'userGroup'
  | 'session'
  | 'metaGroup'

export interface AdminAlertRule {
  id: string
  alertType: string
  ruleKind: 'default' | 'scoped'
  enabled: boolean
  intervalAmount: number | null
  intervalUnit: 'minutes' | 'hours' | 'days' | 'weeks' | null
  scope: Array<{ entityKind: AdminAlertRuleEntityKind; entityId: string }>
  slackChannelIds: string[]
  createdAt: string
  updatedAt: string
}

export interface AdminAlertRulePatch {
  enabled?: boolean
  intervalAmount?: number | null
  intervalUnit?: 'minutes' | 'hours' | 'days' | 'weeks' | null
  slackChannelIds?: string[]
  scope?: Array<{ entityKind: AdminAlertRuleEntityKind; entityId: string }>
}
